import { Attraction } from "typings/microserviceModels/destination-attractions-flex-module";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { UitkCard, UitkCardLink, UitkCardMedia, UitkCardTextSegment } from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-images";
import { MediaAttribution } from "components/utility/MediaAttributionUtil";
import { EntityType, HeartButton } from "components/shared/HeartButton/HeartButton";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import * as React from "react";
import { AggregateRating } from "components/shared/Rating/AggregateRating";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import LazyLoad from "react-lazyload";
import { LazyLoaderPlaceHolder } from "components/utility/LazyLoaderPlaceholderWrapper";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkLayoutFlex } from "uitk-react-layout-flex";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";

export const AttractionCard = ({
  attraction,
  imageUrl,
  context,
  uitkCardClassNames,
  isCountry,
  isActivity,
  attractionsKeyHelper
}: {
  attraction: Attraction;
  imageUrl: string;
  position: number;
  context: ExtendedContextStore;
  uitkCardClassNames: string;
  isCountry: boolean;
  isActivity: boolean;
  attractionsKeyHelper: ItemKeyHelper;
}) => {
  const { name, parentName, media, link, recommendationScore, price } = attraction;
  const description = attraction.description.replace(/\n/g, " ");
  // In the microservice country response...
  //  - source is mis-assigned to origin
  //  - mediaUrlOriginal is absent
  if (isCountry) {
    media.source = media.origin;
    media.mediaUrlOriginal = media.mediaUrl;
  }
  const attribution = MediaAttribution(media, name);
  const totalRatings = attraction.aggregateReviewsAndRatings?.totalRatings;
  const averageRating = attraction.aggregateReviewsAndRatings?.averageRating;

  return (
    <UitkCard className={uitkCardClassNames}>
      <UitkCardLink>
        <TrackedLink
          moduleName="TG.LP.Dest.Attractions"
          href={link}
          rfrr={isActivity ? "Activity.Card.Link" : "Poi.Card.Link"}
        >
          {name}
        </TrackedLink>
      </UitkCardLink>
      <UitkCardMedia className="attractionCard__uitk-card__media-card">
        <UitkFigure ratio={UitkFigureAspectRatioType.R16_9}>
          <LazyLoad placeholder={<LazyLoaderPlaceHolder name={name} />} offset={250}>
            <UitkImage alt={attribution?.attributionAltText} src={imageUrl} />
          </LazyLoad>
          {attribution?.attributionText}
          <HeartButton
            moduleName={"TG.LP.Dest.Attractions"}
            heartableEntity={{
              id: attraction.id,
              name: attraction.name,
              entityType: EntityType.POINT_OF_INTEREST,
              payload: {}
            }}
          />
        </UitkFigure>
      </UitkCardMedia>
      <div className="uitk-card-content uitk-cell">
        {isActivity ? (
          <ActivityCardTitleSegment name={name} price={price} recommendationScore={recommendationScore} />
        ) : (
          <PoiCardTitleSegment
            name={name}
            parentName={parentName}
            isCountry={isCountry}
            averageRating={averageRating}
            totalRatings={totalRatings}
            key={attractionsKeyHelper.next()}
            context={context}
          />
        )}
        {!isActivity && (
          <UitkCardTextSegment>
            <div className="uitk-type-300" dangerouslySetInnerHTML={{ __html: `<p>${description}</p>` }} />
          </UitkCardTextSegment>
        )}
      </div>
    </UitkCard>
  );
};

export const PoiCardTitleSegment = ({
  name,
  parentName,
  isCountry,
  averageRating,
  totalRatings,
  context
}: {
  name: string;
  parentName: string;
  isCountry: boolean;
  averageRating?: number;
  totalRatings?: number;
  context: ExtendedContextStore;
}) => {
  return (
    <UitkCardTextSegment className="uitk-card-separator-bottom">
      <UitkLayoutGrid columns={1} className="uitk-layout-grid-gap-zero">
        <UitkLayoutGridItem>
          <h4 className="uitk-type-heading-400">{name}</h4>
          {isCountry && Boolean(parentName) && <p className="uitk-type-200 poiParentName">{parentName}</p>}
        </UitkLayoutGridItem>
        <UitkLayoutGridItem>
          <AggregateRating rating={averageRating} reviewCount={totalRatings} context={context} />
        </UitkLayoutGridItem>
      </UitkLayoutGrid>
    </UitkCardTextSegment>
  );
};

export const ActivityCardTitleSegment = ({
  name,
  price,
  recommendationScore
}: {
  name: string;
  price: string;
  recommendationScore: number;
}) => {
  const { formatText } = useLocalization();

  return (
    <UitkCardTextSegment>
      <UitkLayoutFlex className="all-b-margin-three">
        <UitkHeading level={4} size={400}>
          {name}
        </UitkHeading>
      </UitkLayoutFlex>

      <UitkLayoutFlex justifyContent="space-between">
        <UitkLayoutFlex direction="column">
          {recommendationScore > 70 && (
            <UitkText size={200}>
              <div
                dangerouslySetInnerHTML={{
                  __html: formatText("destinationActivities.travellersRecommend", recommendationScore)
                }}
              />
            </UitkText>
          )}
        </UitkLayoutFlex>

        {Boolean(price) && (
          <UitkLayoutFlex direction="column">
            <UitkText size={600} weight="bold">
              {price}
            </UitkText>
            <UitkText size={200}>{formatText("destinationActivities.perAdult")}</UitkText>
          </UitkLayoutFlex>
        )}
      </UitkLayoutFlex>
    </UitkCardTextSegment>
  );
};
